/**
 * A brand's own logo, or its initial when we cannot get one.
 *
 * Logos come from logo.dev, which took over the space when Clearbit's Logo API
 * shut down in December 2025. It is used rather than a favicon service because
 * a favicon service can only return what a site publishes — often 64px — and
 * the marks here render up to 40 CSS pixels, which is 80 on a retina screen.
 * GitHub was visibly soft next to OpenAI for exactly that reason.
 *
 * The token is a **publishable** key (`pk_`). logo.dev issues these to be put
 * in client-side URLs; it is not a secret and there is nothing to rotate. It
 * still comes from the environment rather than the source, so a different
 * deployment can use a different account without a code change (§9).
 *
 * The cost is a third party. Every brand and competitor domain a customer
 * types is sent to logo.dev to render an icon, and that is recorded in the
 * data inventory. Fetching once and caching in object storage is the version
 * with no processor in it, and is the honest upgrade before real customer
 * lists are in here.
 */

import { useState } from "react";

const TOKEN = import.meta.env.VITE_LOGO_TOKEN ?? "";

/**
 * Twice the largest rendered size, so a retina screen has real pixels rather
 * than an upscale.
 */
const ICON_SIZE = 128;

/** Deterministic tint from the name, so a brand keeps the same colour. */
export const MONOGRAM_TINTS = [
  "var(--color-tint-teal)",
  "var(--color-tint-coral)",
  "var(--color-tint-rose)",
  "var(--color-tint-orange)",
  "var(--color-brand-wash)",
] as const;

/**
 * Exported so the user monogram tints from the same five, rather than picking
 * its own palette by eye (§15). Same input, same colour, everywhere.
 */
export function monogramTint(name: string): string {
  let hash = 0;
  for (const char of name) {
    hash = (hash + (char.codePointAt(0) ?? 0)) % MONOGRAM_TINTS.length;
  }
  return MONOGRAM_TINTS[hash] ?? MONOGRAM_TINTS[0];
}

/**
 * The logo, or nothing — and nothing is the monogram.
 *
 * There used to be a second service here: with no token configured this fell
 * back to Google's favicon endpoint, so "a deployment without a logo.dev
 * account still shows logos, just softer ones". It could not work and never
 * had. That endpoint 301s to `t{n}.gstatic.com`, a content policy applies to
 * the *redirect target*, and no directive naming `www.google.com` will ever
 * admit it — which is why the fallback failed silently on the one deployment
 * shape it existed for.
 *
 * Removing it is the better answer anyway. A token-less install was sending
 * every brand and competitor domain a customer had typed to a second processor
 * nobody had chosen, to render a 16px favicon we would then upscale. The
 * monogram is a designed state, it is already what a brand with no domain
 * shows, and it costs no third party at all (§11).
 */
function iconUrl(domain: string): string {
  return `https://img.logo.dev/${encodeURIComponent(domain)}?token=${encodeURIComponent(TOKEN)}&size=${String(ICON_SIZE)}&format=png&retina=true`;
}

export function BrandMark({
  name,
  domain,
  size = "md",
}: {
  readonly name: string;
  /** Registrable domain. Empty while someone is still typing one. */
  readonly domain: string;
  readonly size?: "xs" | "sm" | "md" | "lg";
}) {
  /*
   * Keyed by domain, so a row re-used for a different brand starts again rather
   * than inheriting the previous one's failure. Without the key a virtualised
   * or re-sorted list shows a monogram for a logo that was never asked for.
   */
  const [failed, setFailed] = useState<string | null>(null);
  const initial = name.trim().charAt(0).toUpperCase();

  // Nothing typed yet. An empty outline waits; a tinted "?" reads as an error
  // badge on a field the person has not reached.
  if (initial === "" || initial === "?") {
    return <span className={`brand-mark brand-mark-${size}`} aria-hidden="true" />;
  }

  // No domain, no token, or a logo that would not load. All three are the same
  // picture, and it is a letter rather than a torn page.
  if (domain.trim() === "" || TOKEN === "" || failed === domain.trim()) {
    return (
      <span
        className={`brand-mark brand-mark-${size}`}
        /* The one case with no image, so it supplies its own surface. */
        style={{ background: monogramTint(name) }}
        aria-hidden="true"
      >
        {initial}
      </span>
    );
  }

  return (
    <span className={`brand-mark brand-mark-${size}`} aria-hidden="true">
      <img
        src={iconUrl(domain.trim())}
        alt=""
        width={ICON_SIZE}
        height={ICON_SIZE}
        loading="lazy"
        decoding="async"
        // A brand's own site must not learn it was looked up from here.
        referrerPolicy="no-referrer"
        onError={() => {
          // The monogram, not the browser's torn-page icon. A logo that will
          // not load is a missing picture; a broken image reads as a bug.
          setFailed(domain.trim());
        }}
      />
    </span>
  );
}
